import type { ReactNode } from 'react'
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { useDrawer } from '@fourthwall/components'

type MenuDrawerContextType = ReturnType<typeof useDrawer> & {
  activeNavigationGroup: string | null
  setActiveNavigationGroup: (group: string | null) => void
}

type MenuDrawerContextProps = {
  children: ReactNode
}

export const MenuDrawerContext = createContext<MenuDrawerContextType>({
  activeNavigationGroup: null,
  setActiveNavigationGroup: () => {},
  drawerActive: false,
  drawerRoot: document.createElement('div'),
  drawerOpen: () => {},
  drawerClose: () => {},
})

export const MenuDrawerProvider = ({ children }: MenuDrawerContextProps) => {
  const [activeNavigationGroup, setActiveNavigationGroup] = useState<string | null>(null)
  const drawer = useDrawer('menu-drawer-root', { disableBodyScroll: true })
  const location = useLocation()

  useEffect(() => {
    drawer.drawerClose()
  }, [location])

  const value: MenuDrawerContextType = useMemo(
    () => ({
      ...drawer,
      activeNavigationGroup,
      setActiveNavigationGroup,
    }),
    [drawer, activeNavigationGroup, setActiveNavigationGroup],
  )

  return <MenuDrawerContext.Provider value={value}>{children}</MenuDrawerContext.Provider>
}

export const useMenuDrawerContext = () => {
  return useContext(MenuDrawerContext)
}
